<template>
  <li>
    <div
      v-if="!shouldUseFloating"
      @click="handleRegularItemClick"
      :class="[
        'flex items-center justify-between cursor-pointer text-mid-neutral-500 hover:bg-light-neutral-500 rounded-md px-3 py-2',
        { 'bg-light-neutral-500': isActive(item) },
      ]"
    >
      <div class="flex items-center gap-2.5">
        <span class="material-symbols-outlined text-[20px]">
          {{
            hasSubItems && depth === 0
              ? isExpanded
                ? "folder_open"
                : "folder"
              : item.icon
          }}
        </span>
        <span class="truncate block" :title="item.label">{{ item.label }}</span>
      </div>

      <span v-if="hasSubItems && depth === 0" class="material-symbols-outlined">
        {{ isExpanded ? "expand_less" : "expand_more" }}
      </span>
    </div>

    <ul
      v-if="hasSubItems && isExpanded && depth === 0"
      class="pl-4 py-2 border-l-2 border-mid-neutral-50 ml-6 space-y-1"
    >
      <NavigationMenuItem
        v-for="sub in item.subItems"
        :key="sub.path || sub.label"
        :item="sub"
        :depth="depth + 1"
      />
    </ul>

    <FloatingMenu
      trigger="hover"
      :offset="[0, 5]"
      placement="right-start"
      v-else-if="shouldUseFloating"
      :arrow="false"
    >
      <template #activator>
        <div
          class="flex items-center justify-between px-3 py-2 rounded-md hover:bg-light-neutral-500 cursor-pointer text-mid-neutral-500"
          :class="{ 'bg-light-neutral-500': isActive(item) }"
        >
          <div class="flex items-center gap-2.5">
            <span class="material-symbols-outlined text-[20px]">
              {{ item.icon }}
            </span>
            <span class="truncate block" :title="item.label">
              {{ item.label }}
            </span>
          </div>
          <span class="material-symbols-outlined">chevron_right</span>
        </div>
      </template>

      <template #content>
        <div class="w-[200px] p-2 bg-white border rounded shadow-lg">
          <button
            v-for="subSub in item.subItems"
            :key="subSub.path || subSub.label"
            @click="() => handleFloatingMenuItemClick(subSub)"
            class="w-full px-3 py-2 hover:bg-light-neutral-500"
            :class="[{ 'bg-light-neutral-500': isActive(subSub) }]"
          >
            <div class="flex items-center gap-2 text-mid-neutral-500">
              <span class="material-symbols-outlined text-[20px]">
                {{ subSub.icon }}
              </span>
              <span>{{ subSub.label }}</span>
            </div>
          </button>
        </div>
      </template>
    </FloatingMenu>
  </li>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { FloatingMenu } from "ui-components";
import type { MenuItem } from "./navigationMenu";

const props = defineProps<{
  item: MenuItem;
  depth?: number;
}>();
const hasSubItems = (props.item.subItems?.length ?? 0) > 0;

const depth = props.depth ?? 0;

const shouldUseFloating = hasSubItems && depth === 1;
const router = useRouter();
const route = useRoute();

const isExpanded = ref(true);

function handleRegularItemClick() {
  if (!hasSubItems && props.item.path) {
    router.push(props.item.path);
  } else {
    toggleExpand();
  }
}

function handleFloatingMenuItemClick(subItem: MenuItem) {
  if (subItem.path) router.push(subItem.path);
}

function toggleExpand() {
  isExpanded.value = !isExpanded.value;
}

function isActive(item: MenuItem): boolean {
  if (route.path.includes(item.path ?? "/not-implemented")) return true;

  if (item.subItems?.length) {
    return item.subItems.some((sub) => isActive(sub));
  }

  return false;
}
</script>
